import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import remarkFrontmatter from 'remark-frontmatter';
import remarkRehype from 'remark-rehype';
import rehypeStringify from 'rehype-stringify';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { rehypeScrollableRegions } from './scrollable.js';
import { materialiseEmbeds, repointAnchors, schema } from './sanitise.js';
import { visit } from 'unist-util-visit';
import { substituteVariables } from './variables.js';
import { expandSnippets, snippetReferences } from './snippets.js';
import { recogniseEmbed } from './embeds.js';
import { isKnownTextDirective } from './directives.js';
/**
 * Markdown -> HTML, for our directive blocks.
 *
 * This lives in the same package as the parser so the WRITER'S PREVIEW AND THE
 * PUBLISHED PAGE cannot diverge. A preview that renders a block differently from
 * the site is worse than no preview: the writer trusts it and ships something
 * else.
 *
 * Tabs are driven by radio inputs and CSS. No JavaScript ships, so they work
 * with scripting disabled and cannot break the page.
 */
const HINT_LABEL = {
    note: 'Note',
    tip: 'Tip',
    info: 'Note',
    warning: 'Warning',
    danger: 'Danger',
};
const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
const html = (value) => ({ type: 'html', value });
/**
 * A stable, readable id from a title — `#step-preview-it`, not `#step-3`, so a
 * link survives a step being reordered or another one being inserted before it.
 *
 * Hand-rolled rather than a slug library: this package ships to the browser
 * (see the module doc in `pipeline.ts`), and a lowercase-and-hyphenate is all
 * either caller needs.
 */
function slugify(text) {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
/**
 * One id generator per document, so two steps or accordions that happen to
 * share a title still get distinct anchors rather than the second one silently
 * stealing the first's target.
 */
function idFactory() {
    const used = new Set();
    return (prefix, title, fallbackIndex) => {
        const base = title?.trim() ? `${prefix}-${slugify(title)}` : `${prefix}-${fallbackIndex}`;
        let id = base || `${prefix}-${fallbackIndex}`;
        let n = 2;
        while (used.has(id))
            id = `${base}-${n++}`;
        used.add(id);
        return id;
    };
}
/** The `title="..."` a code fence's meta string carries, if any. Reused as a
 * code group's tab label — a filename or "cURL" reads better than "bash". */
function metaTitle(meta) {
    const match = meta ? /(?:^|\s)title="([^"]*)"/.exec(meta) : null;
    return match?.[1]?.trim() || null;
}
/**
 * Remark plugin rendering `:::hint`, `:::expandable`, `::::tabs`, `::::cards`,
 * `::::steps`, `:::file`, `::button[...]` and `:::codegroup` — and resolving
 * `{{variable}}` references first, so directive bodies are substituted too.
 */
export function remarkAdaDirectives(options = {}) {
    return (tree) => {
        /**
         * Before the directive pass, so text inside a hint, a tab panel or a step
         * is already resolved when those containers splice their children. Text
         * nodes only: a code fence or inline code showing `{{syntax}}` is
         * documentation about the syntax, and an undefined name stays literal on
         * screen — a readable name beats a silent hole, and the lint rule
         * `undefined-variable` is what reports it.
         */
        // Snippets first, variables second: a variable inside a snippet body must
        // resolve on the page that shows it, so the spliced blocks have to exist
        // before the substitution pass walks the tree.
        if (options.snippets) {
            expandSnippets(tree, {
                snippets: options.snippets,
                parse: (markdown) => snippetParser.parse(markdown),
            });
        }
        if (options.variables && Object.keys(options.variables).length > 0) {
            substituteVariables(tree, options.variables);
        }
        /**
         * The `srcset`/`sizes` pair for an image, or nothing.
         *
         * Nothing is the common answer and the safe one: an SVG, a GIF, an image
         * hosted somewhere else, an image whose derive failed, or any page
         * rendered by a caller that has no manifest. All of them keep the single
         * `<img src>` that has always worked.
         */
        const responsive = (src) => {
            const srcset = options.images?.srcsets[src];
            return srcset ? { srcset, sizes: options.images.sizes } : null;
        };
        /**
         * Plain Markdown images — `![alt](/assets/...)`.
         *
         * Through `hProperties` rather than by emitting raw HTML, so the alt text
         * and the title keep going through the normal mdast-to-hast conversion and
         * this pass cannot get their escaping wrong. The property names are hast's
         * (`srcSet`), which is what the sanitiser's allowance is written against.
         */
        visit(tree, 'image', (node) => {
            const extra = responsive(node.url);
            if (!extra)
                return;
            const properties = (node.data?.hProperties ?? {});
            node.data = {
                ...node.data,
                hProperties: { ...properties, srcSet: extra.srcset, sizes: extra.sizes },
            };
        });
        let tabGroup = 0;
        let codeGroupCounter = 0;
        // Reset for every document. A module-level counter would keep growing
        // across an entire site build, which is harmless for uniqueness but would
        // make the persistence script's presence depend on flags leaking between
        // unrelated pages — this way each page decides for itself.
        let needsCodeGroupScript = false;
        /** A named tab group appeared, so the one script has a second thing to sync. */
        let needsTabScript = false;
        const anchorId = idFactory();
        visit(tree, (node, index, parent) => {
            if (parent === undefined || index === undefined)
                return;
            const p = parent;
            /**
             * A mermaid fence is a diagram, not code to read.
             *
             * The fence becomes a plain container holding the source. Drawing is the
             * page's job — `drawMermaidDiagrams` runs in the browser on both
             * surfaces (the published site and the writer's preview), themed to the
             * page and with a readable panel when the syntax is wrong. With
             * scripting disabled the source itself stays visible, which keeps the
             * page legible rather than blank.
             *
             * Replaced HERE, before any code highlighter sees it: Expressive Code
             * would otherwise present diagram source as a code sample, complete
             * with a copy button, on every published page.
             */
            const fence = node;
            if (fence.type === 'code' && (fence.lang ?? '').trim().toLowerCase() === 'mermaid') {
                p.children[index] = html(`<div class="sls-mermaid"><pre class="sls-mermaid__src">${escapeHtml(fence.value)}</pre></div>`);
                return;
            }
            // Inline marks that have no bare Markdown syntax: `:underline[x]`,
            // `:highlight[x]`. A `<div>` cannot wrap phrasing content, so these are
            // handled by `hName`/`hProperties` on the node itself rather than by
            // splicing HTML strings into the parent, which is how every container
            // below does it.
            if (node.type === 'textDirective') {
                const t = node;
                if (t.name === 'underline') {
                    t.data = { ...t.data, hName: 'u' };
                    return;
                }
                if (t.name === 'highlight') {
                    t.data = { ...t.data, hName: 'mark' };
                    return;
                }
                // An unrecognised inline directive still shows its text rather than
                // vanishing or leaking `:name[...]` syntax into the page.
                t.data = { ...t.data, hName: 'span', hProperties: { class: 'sls-unknown-directive' } };
                return;
            }
            if (node.type === 'leafDirective') {
                const l = node;
                if (l.name !== 'button')
                    return;
                const attrs = (l.attributes ?? {});
                const href = attrs['href'];
                const variant = attrs['style'] === 'secondary' ? 'secondary' : 'primary';
                const out = [
                    html(href
                        ? `<a class="sls-button sls-button--${variant}" href="${escapeHtml(href)}">`
                        : `<span class="sls-button sls-button--${variant} sls-button--empty">`),
                    ...l.children,
                    html(href ? '</a>' : '</span>'),
                ];
                p.children.splice(index, 1, ...out);
                return;
            }
            const n = node;
            if (n.type !== 'containerDirective')
                return;
            const attrs = (n.attributes ?? {});
            if (n.name === 'hint') {
                const type = HINT_LABEL[attrs['type'] ?? ''] ? attrs['type'] : 'note';
                // An author's own title replaces the generic label rather than sitting
                // above it. "Note / Before you begin" says nothing that "Before you
                // begin" does not.
                const label = attrs['title']?.trim() || HINT_LABEL[type];
                n.data = { ...n.data, hName: 'div', hProperties: { class: `sls-hint sls-hint--${type}` } };
                n.children = [
                    html(`<p class="sls-hint__label">${escapeHtml(label)}</p>`),
                    ...n.children,
                ];
                return;
            }
            if (n.name === 'expandable') {
                // `<details>` puts the body in the DOM whether it is open or not, so
                // this was already crawlable — a crawler reads markup, it does not
                // click. What was missing was a stable link TO the section, which the
                // id below is.
                const id = anchorId('accordion', attrs['title'], index + 1);
                n.data = {
                    ...n.data,
                    hName: 'details',
                    hProperties: { class: 'sls-expandable', id },
                };
                n.children = [
                    html(`<summary>${escapeHtml(attrs['title'] ?? 'Details')}</summary>`),
                    ...n.children,
                ];
                return;
            }
            if (n.name === 'tabs') {
                const tabs = n.children.filter((c) => c.type === 'containerDirective' &&
                    c.name === 'tab');
                if (tabs.length === 0)
                    return;
                const group = `sls-tabs-${++tabGroup}`;
                /**
                 * The tab this group opens on.
                 *
                 * The first, unless one says otherwise — which matters more than it
                 * sounds: a page that means to open on `npm` opening on `yarn` is
                 * wrong on every visit, and an import that could not carry the choice
                 * silently made it for every page.
                 *
                 * The FIRST one marked wins if two are. Two defaults is a mistake in
                 * the source, and picking one predictably beats refusing to render.
                 */
                const chosen = Math.max(0, tabs.findIndex((tab) => {
                    const value = tab.attributes?.['default'];
                    return value !== undefined && value !== 'false';
                }));
                /**
                 * The question this group asks, when it is one the page asks twice.
                 *
                 * `:::tabs{group="package-manager"}` says these tabs answer the same
                 * question as every other group named that — so a reader who chooses
                 * npm once is choosing it for the page. Absent, the group answers only
                 * itself, which is the ordinary case and stays script-free.
                 */
                const syncGroup = n.attributes?.['group']?.trim();
                /** What each tab IS, for syncing: `value`, or the label it shows. */
                const valueOf = (tab, i) => {
                    const attrs = tab.attributes;
                    return (attrs?.['value'] ?? attrs?.['title'] ?? `Tab ${i + 1}`).trim();
                };
                if (syncGroup)
                    needsTabScript = true;
                const out = [
                    html(`<div class="sls-tabs"${syncGroup ? ` data-sync-group="${escapeHtml(syncGroup)}"` : ''}>`),
                ];
                tabs.forEach((tab, i) => {
                    out.push(html(`<input class="sls-tabs__radio" type="radio" name="${group}" id="${group}-${i}"` +
                        (syncGroup ? ` data-value="${escapeHtml(valueOf(tab, i))}"` : '') +
                        `${i === chosen ? ' checked' : ''}>`));
                });
                /**
                 * No `role="tablist"`, deliberately.
                 *
                 * It was there and it was a lie axe caught: a tablist must contain
                 * elements with `role="tab"`, and these are `<label>`s for radio
                 * buttons — because this block is a CSS-only radio group, which is what
                 * makes it work with scripting disabled. Real tab semantics need
                 * `aria-selected` to follow the selection, and nothing here can move it
                 * without JavaScript, so the role promised a keyboard interface the
                 * markup does not implement.
                 *
                 * Left as what it IS: a radio group with labels, which a screen reader
                 * announces as "radio button, iOS, 1 of 2" — true, and usable. The
                 * strip carries no role at all rather than a second one, since the
                 * radios themselves are not inside it.
                 */
                out.push(html('<div class="sls-tabs__list">'));
                tabs.forEach((tab, i) => {
                    const title = escapeHtml(tab.attributes?.['title'] ?? `Tab ${i + 1}`);
                    out.push(html(`<label class="sls-tabs__tab" for="${group}-${i}">${title}</label>`));
                });
                out.push(html('</div>'));
                tabs.forEach((tab, i) => {
                    /**
                     * The tab's title, again, where the site's search can read it.
                     *
                     * The strip above is `<label>`s for the radios, and Pagefind
                     * leaves a label's words out of the index the way it leaves out
                     * every form control's — measured: a reader searching for a tab's
                     * name found nothing, while the editor's index and Ask found the
                     * page. `data-pagefind-index-attrs` names an attribute whose value
                     * is indexed as the page's words, so the name reaches the index
                     * without a second copy of it in the page for anybody to read.
                     */
                    const name = escapeHtml(tab.attributes?.['title'] ?? `Tab ${i + 1}`);
                    out.push(html(`<div class="sls-tabs__panel" data-index="${i}" data-tab="${name}" data-pagefind-index-attrs="data-tab">`));
                    out.push(...tab.children);
                    out.push(html('</div>'));
                });
                out.push(html('</div>'));
                p.children.splice(index, 1, ...out);
                return;
            }
            if (n.name === 'steps') {
                const steps = n.children.filter((c) => c.type === 'containerDirective' &&
                    c.name === 'step');
                if (steps.length === 0)
                    return;
                // A real <ol>, not styled divs. A procedure's order is meaning, not
                // decoration: this way a screen reader announces "list, 4 items" and
                // the position of each one, and the numbers survive with CSS disabled.
                const out = [html('<ol class="sls-steps">')];
                steps.forEach((step, i) => {
                    const title = step.attributes?.['title'];
                    const id = anchorId('step', title, i + 1);
                    out.push(html(`<li class="sls-step" id="${id}">`));
                    // The title is optional: an untitled step is still a numbered step.
                    if (title)
                        out.push(html(`<p class="sls-step__title">${escapeHtml(title)}</p>`));
                    out.push(...step.children);
                    out.push(html('</li>'));
                });
                out.push(html('</ol>'));
                p.children.splice(index, 1, ...out);
                return;
            }
            if (n.name === 'figure') {
                const src = attrs['src'];
                if (!src) {
                    // No image is not a figure. Render the caption as ordinary content
                    // rather than emitting an <img> with no source.
                    n.data = { ...n.data, hName: 'div', hProperties: { class: 'sls-unknown-directive' } };
                    return;
                }
                const out = [html('<figure class="sls-figure">')];
                // width is an attribute, not a style, so it participates in the
                // browser's own aspect-ratio handling and cannot leak into CSS.
                const width = attrs['width'] ? ` width="${escapeHtml(attrs['width'])}"` : '';
                // The same smaller copies a plain Markdown image gets. Written into
                // the raw HTML here because this directive builds its `<img>` itself;
                // the values come from the caller's manifest, never from the page.
                const alt = escapeHtml(attrs['alt'] ?? '');
                const dark = attrs['srcDark'];
                /**
                 * Two sources, chosen by CSS, never by script.
                 *
                 * `<picture>` with a `prefers-color-scheme` media query would be the
                 * tidy answer and is the wrong one here: this site has THREE theme
                 * states, because a reader can override the system with the toggle,
                 * and a `media` attribute cannot see `data-theme`. So both images are
                 * emitted and the stylesheet hides one — which also means a reader
                 * with no JavaScript still gets the right picture, and the toggle
                 * swaps it with no flash.
                 *
                 * The cost is that both are fetched. They are the same illustration at
                 * the same size, and `loading="lazy"` keeps them off the critical
                 * path; a wrong picture on every page is the worse trade.
                 */
                const one = (source, cls) => {
                    const responsiveFor = responsive(source);
                    const attrsFor = responsiveFor
                        ? ` srcset="${escapeHtml(responsiveFor.srcset)}" sizes="${escapeHtml(responsiveFor.sizes)}"`
                        : '';
                    return `<img src="${escapeHtml(source)}" alt="${alt}"${width}${attrsFor} loading="lazy" decoding="async"${cls}>`;
                };
                if (dark) {
                    out.push(html(one(src, ' class="sls-figure__light"')));
                    // Empty alt on the twin: it is the SAME picture, and a screen reader
                    // announcing it twice describes two illustrations that do not exist.
                    out.push(html(one(dark, ' class="sls-figure__dark"').replace(`alt="${alt}"`, 'alt="" aria-hidden="true"')));
                }
                else {
                    out.push(html(one(src, '')));
                }
                // An empty caption must not emit an empty <figcaption>: it would draw
                // the caption's own spacing under every uncaptioned screenshot. 33 of
                // the 35 real figures have exactly that.
                if (n.children.length > 0) {
                    out.push(html('<figcaption>'));
                    out.push(...n.children);
                    out.push(html('</figcaption>'));
                }
                out.push(html('</figure>'));
                p.children.splice(index, 1, ...out);
                return;
            }
            if (n.name === 'embed') {
                const url = attrs['url'];
                const embed = url ? recogniseEmbed(url) : null;
                if (!embed) {
                    // Unrecognised, so it becomes a link rather than a frame. An arbitrary
                    // URL in an iframe on our page is a stranger's JavaScript beside the
                    // reader's session.
                    const safe = url && /^https:\/\//.test(url) ? url : '';
                    n.data = { ...n.data, hName: 'div', hProperties: { class: 'sls-embed sls-embed--link' } };
                    if (safe) {
                        n.children = [
                            html(`<a href="${escapeHtml(safe)}" rel="noopener noreferrer">${escapeHtml(safe)}</a>`),
                            ...n.children,
                        ];
                    }
                    return;
                }
                /**
                 * A placeholder, not the frame.
                 *
                 * The frame is put in after sanitising — see `materialiseEmbeds`. The
                 * schema cannot allow `iframe`, because then an author could write one
                 * with any src they liked; emitting a placeholder and re-checking the URL
                 * on the far side means the only srcs that can appear are ones the
                 * provider allowlist produced.
                 */
                const out = [
                    html(`<div class="sls-embed sls-embed--${embed.provider} sls-ratio-${embed.ratio}" ` +
                        `data-embed="${escapeHtml(url)}">`),
                ];
                // Anything the author put inside stays, as a caption under the frame.
                if (n.children.length > 0) {
                    out.push(html('<div class="sls-embed__caption">'));
                    out.push(...n.children);
                    out.push(html('</div>'));
                }
                out.push(html('</div>'));
                p.children.splice(index, 1, ...out);
                return;
            }
            if (n.name === 'cards') {
                const cards = n.children.filter((c) => c.type === 'containerDirective' &&
                    c.name === 'card');
                const out = [html('<div class="sls-cards">')];
                for (const card of cards) {
                    const a = (card.attributes ?? {});
                    const href = a['href'];
                    const title = escapeHtml(a['title'] ?? '');
                    out.push(html(href
                        ? `<a class="sls-card" href="${escapeHtml(href)}"><span class="sls-card__title">${title}</span>`
                        : `<div class="sls-card"><span class="sls-card__title">${title}</span>`));
                    out.push(...card.children);
                    out.push(html(href ? '</a>' : '</div>'));
                }
                out.push(html('</div>'));
                p.children.splice(index, 1, ...out);
                return;
            }
            if (n.name === 'file') {
                const src = attrs['src'];
                // A name a reader recognises, not the URL: `/assets/9f2a…e1.pdf` says
                // nothing about what they are about to download.
                const name = attrs['name']?.trim() || src?.split('/').pop() || 'Download';
                const out = [html('<div class="sls-file">')];
                out.push(html(src
                    ? `<a class="sls-file__link" href="${escapeHtml(src)}" download>` +
                        `<span class="sls-file__name">${escapeHtml(name)}</span></a>`
                    : `<span class="sls-file__link sls-file__link--empty">` +
                        `<span class="sls-file__name">No file yet</span></span>`));
                if (n.children.length > 0) {
                    out.push(html('<div class="sls-file__caption">'));
                    out.push(...n.children);
                    out.push(html('</div>'));
                }
                out.push(html('</div>'));
                p.children.splice(index, 1, ...out);
                return;
            }
            /**
             * One field of an API reference.
             *
             * A definition list would be the semantic choice for a name and a
             * description — but a field also carries a type, a required flag and a
             * default, and cramming four facts into `<dt>` produces a row nobody
             * can style and a screen reader reads as one run-on term. So: a
             * labelled block, with the prose as its own children so links, code and
             * lists inside a description keep working.
             */
            if (n.name === 'field') {
                const attrs = (n.attributes ?? {});
                const name = attrs['name'] ?? '';
                const type = attrs['type'] ?? '';
                const required = attrs['required'] !== undefined && attrs['required'] !== 'false';
                const fallback = attrs['default'] ?? '';
                const where = attrs['in'] ?? '';
                const head = `<div class="sls-field">` +
                    `<div class="sls-field__head">` +
                    (name ? `<code class="sls-field__name">${escapeHtml(name)}</code>` : '') +
                    (where ? `<span class="sls-field__in">${escapeHtml(where)}</span>` : '') +
                    (type ? `<span class="sls-field__type">${escapeHtml(type)}</span>` : '') +
                    (required ? `<span class="sls-field__required">required</span>` : '') +
                    (fallback
                        ? `<span class="sls-field__default">default <code>${escapeHtml(fallback)}</code></span>`
                        : '') +
                    `</div>` +
                    `<div class="sls-field__body">`;
                p.children.splice(index, 1, html(head), ...n.children, html('</div></div>'));
                return;
            }
            if (n.name === 'codegroup') {
                const codes = n.children.filter((c) => c.type === 'code');
                if (codes.length === 0)
                    return;
                needsCodeGroupScript = true;
                const group = `sls-codegroup-${++codeGroupCounter}`;
                const out = [html(`<div class="sls-codegroup">`)];
                codes.forEach((code, i) => {
                    const lang = (code.lang ?? '').trim() || 'text';
                    out.push(html(`<input class="sls-codegroup__radio" type="radio" name="${group}" ` +
                        `id="${group}-${i}" data-lang="${escapeHtml(lang)}"${i === 0 ? ' checked' : ''}>`));
                });
                // No `role="tablist"` here either, and for the reason written out at the
                // tabs block above: these are labels for radios, not tabs.
                out.push(html('<div class="sls-codegroup__list">'));
                codes.forEach((code, i) => {
                    const lang = (code.lang ?? '').trim() || 'text';
                    const label = escapeHtml(metaTitle(code.meta) ?? lang);
                    out.push(html(`<label class="sls-codegroup__tab" for="${group}-${i}" data-lang="${escapeHtml(lang)}">${label}</label>`));
                });
                out.push(html('</div>'));
                codes.forEach((code, i) => {
                    out.push(html(`<div class="sls-codegroup__panel" data-index="${i}">`));
                    out.push(code);
                    out.push(html('</div>'));
                });
                out.push(html('</div>'));
                p.children.splice(index, 1, ...out);
                return;
            }
            // An unmodelled directive still renders its content rather than vanishing.
            n.data = { ...n.data, hName: 'div', hProperties: { class: 'sls-unknown-directive' } };
        });
        /**
         * The one bit of first-party JavaScript this renderer ships, and only when
         * a page actually used a code group.
         *
         * Tabs everywhere else are radios and CSS — no script, so they work with
         * scripting disabled. A REMEMBERED preference cannot be: nothing reads
         * `localStorage` without running, and the product asks for exactly that
         * ("the public site may remember preferred language locally", PRODUCT.md
         * §11.1). So this is scoped as tightly as the feature allows: it does one
         * thing, touches one key, and every read of `localStorage` is inside a
         * `try` — a browser that refuses storage (private mode, a locked-down
         * profile) gets a code group that still switches tabs, just not one that
         * remembers.
         *
         * It survives the PUBLISHED site's pipeline (`apps/site`'s Astro build
         * allows raw HTML with no sanitiser afterwards, the same as the `<input>`/
         * `<label>` tabs already rely on) but is stripped by THIS package's own
         * sanitiser (`rehypeSanitize` in the pipeline below never allows `script`,
         * on purpose). So the writer's own preview shows working, switchable code
         * groups without the cross-visit memory — a real degradation, not a bug,
         * and the right side to lose it on: nobody's `localStorage` in an editor
         * preview is the one that matters.
         */
        if (needsCodeGroupScript || needsTabScript) {
            /**
             * ONE script for both, because they are one behaviour.
             *
             * A code group syncs on the language it shows; a named tab group syncs
             * on the value its tabs carry. Same question — "the reader chose this
             * once, keep choosing it" — same storage rule, same failure mode, so a
             * second script would be a second place to get the `try` around
             * `localStorage` wrong.
             *
             * Keyed per group name, so choosing npm does not choose macOS: the code
             * groups share one key because a page has one preferred language, and
             * tab groups get `sls-tab:<group>` because a page can ask about a
             * package manager and an operating system in the same breath.
             */
            tree.children.push(html('<script>(function(){try{' +
                'function pick(sel,attr,val){document.querySelectorAll(sel).forEach(function(g){' +
                'var rs=g.querySelectorAll("input[type=radio]");' +
                'for(var i=0;i<rs.length;i++){if(rs[i].getAttribute(attr)===val){rs[i].checked=true;break;}}' +
                '});}' +
                'function read(k){try{return localStorage.getItem(k);}catch(e){return null;}}' +
                'function write(k,v){try{localStorage.setItem(k,v);}catch(e){}}' +
                'var CK="sls-code-lang";' +
                'var stored=read(CK);if(stored)pick(".sls-codegroup","data-lang",stored);' +
                'document.querySelectorAll(".sls-codegroup").forEach(function(g){' +
                'g.addEventListener("change",function(e){' +
                'var l=e.target&&e.target.getAttribute&&e.target.getAttribute("data-lang");' +
                'if(!l)return;write(CK,l);pick(".sls-codegroup","data-lang",l);});});' +
                'var seen={};' +
                'document.querySelectorAll(".sls-tabs[data-sync-group]").forEach(function(g){' +
                'var name=g.getAttribute("data-sync-group");if(!name)return;' +
                'var sel=\'.sls-tabs[data-sync-group="\'+name+\'"]\';var key="sls-tab:"+name;' +
                'if(!seen[name]){seen[name]=1;var v=read(key);if(v)pick(sel,"data-value",v);}' +
                'g.addEventListener("change",function(e){' +
                'var v=e.target&&e.target.getAttribute&&e.target.getAttribute("data-value");' +
                'if(!v)return;write(key,v);pick(sel,"data-value",v);});});' +
                '}catch(e){}})();</script>'));
        }
    };
}
const buildRenderer = (options) => unified()
    .use(remarkParse)
    .use(remarkFrontmatter, ['yaml'])
    .use(remarkGfm)
    .use(remarkDirective)
    .use(remarkAdaDirectives, options)
    // allowDangerousHtml + rehypeRaw: our directive plugin emits HTML nodes, and
    // GitBook-imported content contains raw HTML that must survive.
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    /**
     * Sanitise, and only then put our own frames in.
     *
     * This renderer used to pass author HTML through untouched, so a page holding
     * `<script>`, an `onerror` handler or a `javascript:` link produced exactly
     * that — and this output is injected into the writer with
     * `dangerouslySetInnerHTML`, where the reader's session lives. Content here is
     * written by importers and agents as well as people.
     *
     * The order matters and is the security property: the schema cannot allow
     * `iframe`, or an author could write one, so embeds are materialised
     * afterwards from a placeholder whose URL is re-checked against the provider
     * allowlist.
     */
    .use(rehypeSanitize, schema)
    .use(materialiseEmbeds)
    .use(repointAnchors)
    /**
     * After the sanitiser, beside the other two transforms that add our own
     * markup rather than an author's. Not a security property, and the first
     * version of this comment claimed one: `tabIndex` is in the default
     * schema's global allowlist, so this survives on either side of the
     * sanitiser — measured by moving it, which changed nothing. It sits here
     * because this is where post-sanitise work goes.
     *
     * Here as well as on the published site, so the preview shows what a reader
     * gets: a wide table scrolls in the editor too, and a writer using a
     * keyboard has the same right to reach the end of it.
     */
    .use(rehypeScrollableRegions)
    .use(rehypeStringify, { allowDangerousHtml: true });
/** Parses snippet BODIES the way the page itself was parsed — syntax only. */
const snippetParser = unified().use(remarkParse).use(remarkGfm).use(remarkDirective);
/**
 * Which shared blocks a page references, from text.
 *
 * The same parser the renderer expands with, so the two cannot disagree about
 * what a reference looks like. `snippetReferences` already answers this from a
 * TREE, for the linter; every other caller has a string and no parser of its
 * own, and the one that went without built a regular expression instead.
 *
 * That regular expression was `^::snippet\{name="([^"]+)"\}$`, and a directive
 * has more legal spellings than that: single quotes, no quotes, spaces inside
 * the braces, another attribute alongside. Each of those is expanded for a
 * reader and was invisible to the caller matching text — so a page's shared
 * words were on the published site and missing from the editor's search and
 * from what Ask retrieves.
 *
 * TRANSITIVE, because a block may include a block. The renderer follows those
 * (to a depth); a single pass over the text does not, so the inner block's
 * words went the same way as the others.
 */
export function snippetReferencesIn(markdown, bodies = {}, depth = 4) {
    const found = new Set();
    const walk = (text, left) => {
        if (left < 0)
            return;
        for (const name of snippetReferences(snippetParser.parse(text))) {
            if (found.has(name))
                continue;
            found.add(name);
            const body = bodies[name];
            if (body !== undefined)
                walk(body, left - 1);
        }
    };
    walk(markdown, depth);
    return found;
}
/** The common case — no variables — built once and reused. */
const renderer = buildRenderer({});
/**
 * Render a page body to HTML. Frontmatter is stripped, not rendered.
 *
 * `variables`, when given, resolve `{{name}}` in page text — the site build
 * passes the published tree's, the preview endpoints pass the draft's. A
 * per-call pipeline for that case rather than a cache: previews render on a
 * debounce and pages at build time, so construction is nowhere near the cost
 * of being handed a stale value.
 */
export function renderToHtml(markdown, options = {}) {
    // Snippets need the bespoke pipeline even when the MAP is empty: an empty
    // map still means "expand references" — a missing snippet must render as
    // its visible placeholder, not as a bare generic div. Only a call with no
    // options at all takes the shared fast path.
    const bespoke = (options.variables !== undefined && Object.keys(options.variables).length > 0) ||
        options.snippets !== undefined ||
        options.images !== undefined;
    return String((bespoke ? buildRenderer(options) : renderer).processSync(markdown));
}
//# sourceMappingURL=render.js.map